/**
 * LOT IMAGE SELECTION — one place that knows which frames are internal vs. content.
 *
 * CONVENTION (SortBuddy / HiBid): images are named "{sku}_NN.<ext>" and are generated in order
 * (see hibidParser.generateImageFilenames: 700001_00, 700001_01, 700001_02 …).
 *   _00  = SKU barcode / reference frame — internal, never shown to bidders
 *   _01+ = actual item photos (content)
 * Detection is therefore a FILENAME SUFFIX check (not an array position, not a flag on the
 * object). Position is unreliable: it only works if _00 happens to sort first.
 *
 * Four surfaces had four different implementations of this (regex skip, positional slice(1),
 * find('_01') with ad-hoc fallbacks, and a raw images[0] — which is how the bid modal ended up
 * showing a barcode). Same discipline as computeOpeningBid: one helper, no drift.
 */

/** Matches the internal "_00" reference frame, tolerating query strings (Cloudinary etc.). */
const INTERNAL_IMAGE_RE = /_00\.[a-z0-9]{2,5}(\?|$)/i;

export const isInternalImage = (url: string): boolean => INTERNAL_IMAGE_RE.test(url);

// ───────────────────────────────────────────────────────────────────────────
// FILENAME RULES
//
// Everything below is about the NAME of the file, never its position in the
// array. Kept here because this is already the file that knows the naming
// convention; audit-lots.mjs carries the same rules in lot-image-names.mjs for
// Node. If you change IMAGE_FILENAME_RE, change it there in the same commit.
// ───────────────────────────────────────────────────────────────────────────

/**
 * The filename inside a Firebase Storage download URL.
 *
 *   https://firebasestorage.googleapis.com/v0/b/<bucket>/o/
 *     auctions%2F<auctionId>%2Fimages%2F<folder>%2F700001_00.jpg?alt=media&token=…
 *
 * The object path is percent-encoded in the /o/ segment.
 */
export const filenameFromUrl = (url?: string | null): string => {
  const s = String(url ?? '');
  try {
    const u = new URL(s);
    const m = u.pathname.match(/\/o\/(.+)$/);
    const objPath = m ? decodeURIComponent(m[1]) : decodeURIComponent(u.pathname);
    return objPath.split('/').filter(Boolean).pop() || '';
  } catch {
    const noQuery = s.split('?')[0];
    const base = noQuery.split('/').filter(Boolean).pop() || '';
    try { return decodeURIComponent(base); } catch { return base; }
  }
};

/**
 * {sku}_{NN}.{ext} and NOTHING else — anchored at both ends on purpose.
 *
 * A loose match is how a DERIVED file gets mistaken for an original and appended
 * to a lot's images[]: `700001_00_200x200.jpg` from a resize extension,
 * `700001_00 (1).jpg` from a re-download, `.DS_Store`, `Thumbs.db`. The previous
 * rule was `filename.split('_')[0]`, which accepts every one of those.
 *
 * THE SKU GROUP IS [A-Za-z0-9-], NOT \d — widened 2026-09-02.
 * Numeric SKUs (700001) are only one of the conventions in use. The slot sale
 * uses JOE104-SLOT-001, and a digits-only rule silently skipped every one of its
 * files: UpdateImages scanned the folder, parsed nothing, and refused — correct
 * behaviour from the guard, but the button could never work for that sale.
 *
 * The widening is deliberately narrow. Letters, digits and HYPHEN only:
 *   - no UNDERSCORE, so `700001_00_200x200.jpg` still fails (the second group
 *     must be digits then the extension, and `00_200x200` is not)
 *   - no SPACE or PARENTHESES, so `700001_00 (1).jpg` still fails
 *   - no DOT, so `.DS_Store` and `Thumbs.db` still fail
 * Those three are pinned as test cases in __verify__/lotImages.test.ts so the
 * next person to widen this has to look at what it is for.
 */
export const IMAGE_FILENAME_RE = /^([A-Za-z0-9-]+)_(\d+)\.[A-Za-z0-9]+$/;

export interface ParsedImageName { sku: string; index: number; }

/** → { sku, index } for a conforming filename, else null. */
export const parseImageFilename = (filename?: string | null): ParsedImageName | null => {
  const m = IMAGE_FILENAME_RE.exec(String(filename ?? ''));
  return m ? { sku: m[1], index: Number(m[2]) } : null;
};

/**
 * Comparator for image URLs, ordering by FILENAME rather than by the URL string.
 *
 * Sorting the URLs themselves is meaningless: a Firebase download URL ends
 * `…%2F700001_01.jpg?alt=media&token=<random uuid>`, so a plain `.sort()` orders
 * by the random token. Two runs of UpdateImages over identical files could
 * therefore produce different orders — and a _00 barcode frame could land first,
 * which is exactly how a barcode becomes a cover photo.
 */
export const byImageFilename = (a: string, b: string): number => {
  const fa = filenameFromUrl(a);
  const fb = filenameFromUrl(b);
  const pa = parseImageFilename(fa);
  const pb = parseImageFilename(fb);
  if (pa && pb) {
    if (pa.sku !== pb.sku) return pa.sku.localeCompare(pb.sku, 'en', { numeric: true });
    return pa.index - pb.index;
  }
  if (pa) return -1;   // conforming names sort ahead of anything unparseable
  if (pb) return 1;
  return fa.localeCompare(fb, 'en', { numeric: true });
};

/** All real content photos, internal frames removed. Use for galleries. */
export const contentImages = (images?: string[] | null): string[] =>
  (images || []).filter((u) => !isInternalImage(u));

/**
 * The cover/thumbnail: the FIRST content photo (_01 …), skipping the _00 barcode frame.
 *
 * Fallback when a lot has ONLY an internal frame (no content photo):
 *   - default            → '' so the caller renders its placeholder. Public-facing tiles
 *                          deliberately prefer a placeholder over showing a barcode.
 *   - { fallbackToAny }  → the first image whatever it is, for staff/fulfillment views where
 *                          seeing *something* (even the barcode) beats seeing nothing.
 */
export const firstContentImage = (
  images?: string[] | null,
  opts?: { fallbackToAny?: boolean },
): string => {
  const list = images || [];
  const content = list.find((u) => !isInternalImage(u));
  if (content) return content;
  return opts?.fallbackToAny ? (list[0] || '') : '';
};

// ───────────────────────────────────────────────────────────────────────────
// APPLYING A STORAGE SCAN TO LOTS
//
// UpdateImages used to do this inline as
//     images: imageMap[lot.sku] || []
// which REPLACES a lot's images with whatever the scan produced — including an
// empty array when the scan found nothing at all. On 2026-08-25 a scan of an
// empty Storage prefix wrote `[]` over 104 lots that were carrying Cloudinary
// URLs, and reported "Images updated successfully!".
//
// The invariant this helper exists to enforce: THIS OPERATION ONLY ADDS.
// A scan that has nothing to say about a lot leaves that lot alone. Removing an
// image is not something an image-scanner is ever asked to do, so it cannot.
// ───────────────────────────────────────────────────────────────────────────

export interface LotLike { sku: string; images?: string[] }

export interface ScanApplyResult<T extends LotLike> {
  lots: T[];
  /** Lots the scan supplied images for. */
  updated: number;
  /** Lots the scan had nothing for, which kept the images they already had. */
  preserved: number;
  withImagesBefore: number;
  withImagesAfter: number;
}

/**
 * Merge a SKU→URLs map from a Storage scan into a lots array, additively.
 * Never returns a lot with fewer images than it started with.
 */
export const applyScannedImages = <T extends LotLike>(
  lots: T[] | null | undefined,
  imageMap: Record<string, string[]>,
): ScanApplyResult<T> => {
  const list = lots || [];
  let updated = 0;
  let preserved = 0;

  const next = list.map((lot) => {
    const existing = (lot.images || []).filter(Boolean);
    const found = (imageMap[lot.sku] || []).filter(Boolean);
    if (!found.length) {
      if (existing.length) preserved++;
      return lot;                                   // untouched — never blanked
    }
    updated++;
    return { ...lot, images: [...found].sort(byImageFilename) };
  });

  const countWith = (ls: T[]) => ls.filter((l) => (l.images || []).filter(Boolean).length > 0).length;
  return {
    lots: next,
    updated,
    preserved,
    withImagesBefore: countWith(list),
    withImagesAfter: countWith(next),
  };
};
